// Asistente con IA: el organizador describe sus condiciones de venta y recibe
// una configuración sugerida (fechas, aforo, límite por persona, sala de espera).
import { useState } from "react";
import { Sparkles, Loader2, AlertTriangle } from "lucide-react";
import { recommendSalesSettings } from "@/lib/sales-advisor.functions";
import { supabase } from "@/integrations/supabase/client";
import type { WizardAdvanced, WizardState } from "@/lib/event-wizard";

type Rec = Awaited<ReturnType<typeof recommendSalesSettings>>;

/** ISO → valor para <input type="datetime-local"> en hora local. */
function toLocalInput(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const fmt = (iso: string) =>
  iso ? new Date(iso).toLocaleString("es-CR", { dateStyle: "medium", timeStyle: "short" }) : "Al publicar";

export function SalesAdvisor({ s, setAdv }: {
  s: WizardState;
  setAdv: <K extends keyof WizardAdvanced>(k: K, v: WizardAdvanced[K]) => void;
}) {
  const [conditions, setConditions] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rec, setRec] = useState<Rec | null>(null);
  const [applied, setApplied] = useState(false);

  async function ask() {
    setError(""); setRec(null); setApplied(false);
    if (conditions.trim().length < 5) { setError("Contanos un poco más sobre cómo querés vender"); return; }
    if (!s.date) { setError("Primero elegí la fecha del evento en el paso 1"); return; }
    setLoading(true);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      const r = await recommendSalesSettings({
        data: {
          conditions: conditions.trim(),
          eventDate: s.date,
          eventTime: s.startTime || "20:00",
          timezone: s.timezone,
          isVirtual: s.isVirtual,
          ticketTypes: s.tickets.map((t) => ({
            name: t.name || "Entrada",
            price: t.isFree ? 0 : parseFloat(t.price) || 0,
            total: parseInt(t.total) || 0,
          })),
        },
        headers: token ? { authorization: `Bearer ${token}` } : undefined,
      } as any);
      setRec(r);
    } catch (e: any) {
      setError(e?.message || "No pudimos generar la recomendación. Probá de nuevo.");
    } finally {
      setLoading(false);
    }
  }

  function apply() {
    if (!rec) return;
    setAdv("salesStart", toLocalInput(rec.salesStartAt));
    setAdv("salesEnd", toLocalInput(rec.salesEndAt));
    setAdv("capacity", String(rec.capacity));
    setAdv("maxPerUser", String(rec.maxPerUser));
    setAdv("waitingRoomEnabled", rec.waitingRoomEnabled);
    setAdv("waitingRoomThreshold", String(rec.waitingRoomThreshold));
    setApplied(true);
  }

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
        <Sparkles className="h-4 w-4 text-primary" /> Sugerir configuración con IA
      </p>
      <label htmlFor="sales-conditions" className="block text-xs text-muted-foreground">
        Describí cómo querés vender. Ej: "Preventa desde el lunes, el lugar tiene 300 personas, esperamos mucha demanda, máximo 4 por persona".
      </label>
      <textarea
        id="sales-conditions" rows={3} maxLength={1500} value={conditions}
        onChange={(e) => setConditions(e.target.value)}
        className="w-full resize-none rounded-xl border border-border bg-secondary px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
      />
      <button type="button" onClick={ask} disabled={loading}
        className="flex w-full items-center justify-center gap-2 rounded-xl gradient-primary py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50">
        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Pensando…</> : "Recomendar configuración"}
      </button>
      {error && <p role="alert" className="text-xs font-medium text-destructive">{error}</p>}

      {rec && (
        <div className="space-y-2 rounded-xl border border-border bg-card p-3 text-sm">
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <dt className="text-muted-foreground">Inicio de venta</dt><dd className="text-foreground">{fmt(rec.salesStartAt)}</dd>
            <dt className="text-muted-foreground">Fin de venta</dt><dd className="text-foreground">{fmt(rec.salesEndAt)}</dd>
            <dt className="text-muted-foreground">Aforo</dt><dd className="text-foreground">{rec.capacity}</dd>
            <dt className="text-muted-foreground">Máx. por persona</dt><dd className="text-foreground">{rec.maxPerUser}</dd>
            <dt className="text-muted-foreground">Sala de espera</dt>
            <dd className="text-foreground">{rec.waitingRoomEnabled ? `Sí, desde ${rec.waitingRoomThreshold} a la vez` : "No"}</dd>
          </dl>
          {rec.rationale && <p className="text-xs text-muted-foreground">{rec.rationale}</p>}
          {rec.warnings.map((w, i) => (
            <p key={i} className="flex items-start gap-1.5 text-xs text-warning"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{w}</p>
          ))}
          <button type="button" onClick={apply} disabled={applied}
            className="w-full rounded-lg border border-border bg-secondary py-2.5 text-xs font-semibold text-secondary-foreground disabled:opacity-60">
            {applied ? "✓ Aplicado — podés ajustar cada campo abajo" : "Aplicar esta configuración"}
          </button>
        </div>
      )}
    </div>
  );
}
